'use client';
import React, { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { campaignService, Campaign, CampaignStatus } from '@/lib/supabase/campaignService';

const STATUS_COLORS: Record<CampaignStatus, { bg: string; text: string; dot: string }> = {
  active: { bg: 'rgba(34,197,94,0.12)', text: '#16a34a', dot: '#22c55e' },
  draft: { bg: 'rgba(148,163,184,0.15)', text: '#64748b', dot: '#94a3b8' },
  paused: { bg: 'rgba(251,191,36,0.15)', text: '#d97706', dot: '#fbbf24' },
  archived: { bg: 'rgba(239,68,68,0.12)', text: '#dc2626', dot: '#ef4444' },
};

function StatusBadge({ status }: { status: CampaignStatus }) {
  const c = STATUS_COLORS[status];
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold capitalize"
      style={{ background: c.bg, color: c.text, fontSize: '11px' }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: c.dot }} />
      {status}
    </span>
  );
}

export default function CampaignsDashboard() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await campaignService.getAll();
    setCampaigns(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id: string, status: CampaignStatus) => {
    const ok = await campaignService.updateStatus(id, status);
    if (ok) {
      toast.success(`Campaign ${status === 'active' ? 'resumed' : status}.`);
      load();
    } else {
      toast.error('Failed to update status.');
    }
  };

  const handleDuplicate = async (id: string) => {
    toast.loading('Duplicating campaign...');
    const dup = await campaignService.duplicate(id);
    toast.dismiss();
    if (dup) {
      toast.success('Campaign duplicated!');
      load();
    } else {
      toast.error('Failed to duplicate campaign.');
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const ok = await campaignService.delete(id);
    setDeletingId(null);
    setConfirmDelete(null);
    if (ok) { toast.success('Campaign deleted.'); load(); }
    else toast.error('Failed to delete campaign.');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-bold text-foreground" style={{ fontSize: '22px' }}>Campaigns</h1>
          <p className="text-muted-foreground mt-0.5" style={{ fontSize: '13px' }}>
            Create and manage QR campaign banners for schools and events.
          </p>
        </div>
        <Link
          href="/campaigns/new"
          className="btn-primary flex items-center gap-2 px-5 py-2.5 rounded-2xl font-semibold self-start sm:self-auto"
          style={{ fontSize: '14px' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="8" y1="2" x2="8" y2="14" />
            <line x1="2" y1="8" x2="14" y2="8" />
          </svg>
          New Campaign
        </Link>
      </div>

      {/* Stats Row */}
      {!loading && campaigns.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: campaigns.length, color: '#2563eb' },
            { label: 'Active', value: campaigns.filter(c => c.campaignStatus === 'active').length, color: '#16a34a' },
            { label: 'Total Scans', value: campaigns.reduce((s, c) => s + c.qrScans, 0), color: '#7c3aed' },
            { label: 'Shares', value: campaigns.reduce((s, c) => s + c.shareInitiated, 0), color: '#d97706' },
          ].map(stat => (
            <div key={stat.label} className="bg-card border border-border rounded-2xl p-4">
              <p className="text-muted-foreground font-medium" style={{ fontSize: '11px' }}>{stat.label}</p>
              <p className="font-bold mt-1" style={{ fontSize: '22px', color: stat.color }}>{stat.value.toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      )}

      {/* Empty */}
      {!loading && campaigns.length === 0 && (
        <div className="bg-card border border-border rounded-3xl p-12 text-center">
          <div className="w-16 h-16 rounded-2xl gradient-primary-soft flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
              <rect x="3" y="3" width="10" height="10" rx="2" />
              <rect x="15" y="3" width="10" height="10" rx="2" />
              <rect x="3" y="15" width="10" height="10" rx="2" />
              <path d="M15 19h10M20 15v8" />
            </svg>
          </div>
          <h3 className="font-bold text-foreground" style={{ fontSize: '16px' }}>No campaigns yet</h3>
          <p className="text-muted-foreground mt-1 mb-5" style={{ fontSize: '13px' }}>
            Create your first campaign to generate a QR code for your banner.
          </p>
          <Link href="/campaigns/new" className="btn-primary px-6 py-2.5 rounded-2xl font-semibold" style={{ fontSize: '14px' }}>
            Create First Campaign
          </Link>
        </div>
      )}

      {/* Campaign Table — Desktop */}
      {!loading && campaigns.length > 0 && (
        <>
          <div className="hidden md:block bg-card border border-border rounded-3xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-5 py-3.5 text-muted-foreground font-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Campaign</th>
                  <th className="text-left px-4 py-3.5 text-muted-foreground font-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</th>
                  <th className="text-right px-4 py-3.5 text-muted-foreground font-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Scans</th>
                  <th className="text-left px-4 py-3.5 text-muted-foreground font-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Created</th>
                  <th className="text-right px-5 py-3.5 text-muted-foreground font-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c, i) => (
                  <tr key={c.id} className={`border-b border-border last:border-0 hover:bg-secondary/40 transition-colors`}>
                    <td className="px-5 py-4">
                      <div>
                        <p className="font-semibold text-foreground" style={{ fontSize: '14px' }}>{c.name}</p>
                        <p className="text-muted-foreground mt-0.5" style={{ fontSize: '11px' }}>
                          {campaignService.getCampaignUrl(c.slug)}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4"><StatusBadge status={c.campaignStatus} /></td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-semibold text-foreground" style={{ fontSize: '14px' }}>{c.qrScans.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 text-muted-foreground" style={{ fontSize: '12px' }}>
                      {new Date(c.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <Link href={`/campaigns/${c.id}`} className="btn-ghost px-3 py-1.5 rounded-xl text-xs font-semibold">Manage</Link>
                        <Link href={`/campaigns/${c.id}/qr`} className="btn-ghost px-3 py-1.5 rounded-xl text-xs font-semibold">QR</Link>
                        <a href={`/c/${c.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost px-3 py-1.5 rounded-xl text-xs font-semibold">Test</a>
                        <button onClick={() => handleDuplicate(c.id)} className="btn-ghost px-3 py-1.5 rounded-xl text-xs font-semibold">Duplicate</button>
                        {c.campaignStatus === 'active' && (
                          <button onClick={() => handleStatusChange(c.id, 'paused')} className="px-3 py-1.5 rounded-xl text-xs font-semibold" style={{ background: 'rgba(251,191,36,0.12)', color: '#d97706' }}>Pause</button>
                        )}
                        {c.campaignStatus === 'paused' && (
                          <button onClick={() => handleStatusChange(c.id, 'active')} className="px-3 py-1.5 rounded-xl text-xs font-semibold" style={{ background: 'rgba(34,197,94,0.12)', color: '#16a34a' }}>Resume</button>
                        )}
                        {c.campaignStatus === 'draft' && (
                          <button onClick={() => handleStatusChange(c.id, 'active')} className="px-3 py-1.5 rounded-xl text-xs font-semibold" style={{ background: 'rgba(37,99,235,0.12)', color: '#2563eb' }}>Publish</button>
                        )}
                        <button onClick={() => setConfirmDelete(c.id)} className="px-3 py-1.5 rounded-xl text-xs font-semibold" style={{ background: 'rgba(239,68,68,0.1)', color: '#dc2626' }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Campaign Cards — Mobile */}
          <div className="md:hidden space-y-3">
            {campaigns.map(c => (
              <div key={c.id} className="bg-card border border-border rounded-2xl p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate" style={{ fontSize: '14px' }}>{c.name}</p>
                    <p className="text-muted-foreground mt-0.5 truncate" style={{ fontSize: '11px' }}>{campaignService.getCampaignUrl(c.slug)}</p>
                  </div>
                  <StatusBadge status={c.campaignStatus} />
                </div>
                <div className="flex items-center gap-4 text-muted-foreground" style={{ fontSize: '12px' }}>
                  <span><span className="font-semibold text-foreground">{c.qrScans}</span> scans</span>
                  <span><span className="font-semibold text-foreground">{c.shareInitiated}</span> shares</span>
                  <span>{new Date(c.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link href={`/campaigns/${c.id}`} className="btn-ghost px-3 py-2 rounded-xl text-xs font-semibold">Manage</Link>
                  <Link href={`/campaigns/${c.id}/qr`} className="btn-ghost px-3 py-2 rounded-xl text-xs font-semibold">QR</Link>
                  <a href={`/c/${c.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost px-3 py-2 rounded-xl text-xs font-semibold">Test</a>
                  <button onClick={() => handleDuplicate(c.id)} className="btn-ghost px-3 py-2 rounded-xl text-xs font-semibold">Duplicate</button>
                  {c.campaignStatus === 'active' && (
                    <button onClick={() => handleStatusChange(c.id, 'paused')} className="px-3 py-2 rounded-xl text-xs font-semibold" style={{ background: 'rgba(251,191,36,0.12)', color: '#d97706' }}>Pause</button>
                  )}
                  {c.campaignStatus === 'paused' && (
                    <button onClick={() => handleStatusChange(c.id, 'active')} className="px-3 py-2 rounded-xl text-xs font-semibold" style={{ background: 'rgba(34,197,94,0.12)', color: '#16a34a' }}>Resume</button>
                  )}
                  {c.campaignStatus === 'draft' && (
                    <button onClick={() => handleStatusChange(c.id, 'active')} className="px-3 py-2 rounded-xl text-xs font-semibold" style={{ background: 'rgba(37,99,235,0.12)', color: '#2563eb' }}>Publish</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Delete Confirm Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-card border border-border rounded-3xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-bold text-foreground" style={{ fontSize: '16px' }}>Delete Campaign?</h3>
            <p className="text-muted-foreground mt-2" style={{ fontSize: '13px' }}>
              This will permanently delete the campaign and all its analytics. This action cannot be undone.
            </p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setConfirmDelete(null)} className="btn-ghost flex-1 py-2.5 rounded-2xl font-semibold" style={{ fontSize: '14px' }}>Cancel</button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={deletingId === confirmDelete}
                className="flex-1 py-2.5 rounded-2xl font-semibold text-white disabled:opacity-70"
                style={{ fontSize: '14px', background: '#dc2626' }}
              >
                {deletingId === confirmDelete ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
